import { FilePlus2, ShieldCheck, UploadCloud } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { localize, type AcceptedFileType, type Locale } from '../../../domain/tools/types';
import '../workspace.css';

export type FilePickerIssueSeverity = 'error' | 'warning';

export interface FilePickerIssue {
  readonly code: string;
  readonly severity: FilePickerIssueSeverity;
  readonly message: string;
}

export interface ValidatedPickedFile {
  readonly file: File;
  readonly warnings: readonly FilePickerIssue[];
}

export interface RejectedPickedFile {
  readonly file: File;
  readonly issues: readonly FilePickerIssue[];
}

export interface FilePickerSelection {
  readonly accepted: readonly ValidatedPickedFile[];
  readonly rejected: readonly RejectedPickedFile[];
}

export interface FilePickerValidationContext {
  readonly signal: AbortSignal;
  readonly acceptedFileTypes: readonly AcceptedFileType[];
}

export type FilePickerValidator = (
  file: File,
  context: FilePickerValidationContext,
) => readonly FilePickerIssue[] | Promise<readonly FilePickerIssue[]>;

export interface FilePickerProps {
  readonly acceptedFileTypes: readonly AcceptedFileType[];
  readonly multiple?: boolean;
  readonly maxFiles?: number;
  readonly existingFileCount?: number;
  readonly existingFileNames?: readonly string[];
  readonly allowDuplicateNames?: boolean;
  readonly disabled?: boolean;
  readonly busy?: boolean;
  readonly capture?: 'user' | 'environment';
  readonly title?: string;
  readonly description?: string;
  readonly validator?: FilePickerValidator;
  readonly onSelection: (selection: FilePickerSelection) => void;
  readonly onValidationStateChange?: (validating: boolean) => void;
}

function currentLocale(language: string): Locale {
  return language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot).toLowerCase();
}

function fileMatchesAcceptedType(
  file: File,
  acceptedFileTypes: readonly AcceptedFileType[],
): boolean {
  if (acceptedFileTypes.length === 0) {
    return true;
  }

  const extension = extensionOf(file.name);
  const mimeType = file.type.toLowerCase();
  return acceptedFileTypes.some(
    (accepted) =>
      accepted.extensions.some((candidate) => candidate.toLowerCase() === extension) ||
      (mimeType !== '' &&
        accepted.mimeTypes.some((candidate) => candidate.toLowerCase() === mimeType)),
  );
}

function acceptAttribute(acceptedFileTypes: readonly AcceptedFileType[]): string {
  return Array.from(
    new Set(
      acceptedFileTypes.flatMap((accepted) => [
        ...accepted.extensions,
        ...accepted.mimeTypes,
      ]),
    ),
  ).join(',');
}

export function FilePicker({
  acceptedFileTypes,
  multiple = false,
  maxFiles = multiple ? Number.MAX_SAFE_INTEGER : 1,
  existingFileCount = 0,
  existingFileNames = [],
  allowDuplicateNames = true,
  disabled = false,
  busy = false,
  capture,
  title,
  description,
  validator,
  onSelection,
  onValidationStateChange,
}: FilePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = currentLocale(i18n.resolvedLanguage ?? i18n.language);
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const issuesId = `${inputId}-issues`;
  const inputRef = useRef<HTMLInputElement>(null);
  const validationController = useRef<AbortController | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validating, setValidating] = useState(false);
  const [displayedIssues, setDisplayedIssues] = useState<readonly FilePickerIssue[]>([]);
  const isBusy = busy || validating;

  const acceptedLabels = useMemo(
    () => acceptedFileTypes.map(({ label }) => localize(label, locale)).join(', '),
    [acceptedFileTypes, locale],
  );
  const accept = useMemo(() => acceptAttribute(acceptedFileTypes), [acceptedFileTypes]);

  useEffect(
    () => () => {
      validationController.current?.abort();
      validationController.current = null;
    },
    [],
  );

  const validateSelection = async (files: readonly File[]) => {
    validationController.current?.abort();
    const controller = new AbortController();
    validationController.current = controller;
    setValidating(true);
    onValidationStateChange?.(true);

    const rejected: RejectedPickedFile[] = [];
    const candidates: File[] = [];
    const knownNames = new Set(existingFileNames.map((name) => name.toLocaleLowerCase('en')));
    const maximum = multiple ? maxFiles : 1;
    const availableSlots = Math.max(0, maximum - existingFileCount);

    for (const file of files) {
      const issues: FilePickerIssue[] = [];
      if (candidates.length >= availableSlots) {
        issues.push({
          code: 'file.tooMany',
          severity: 'error',
          message: t('errors.file.tooMany', { maximum }),
        });
      }
      if (file.size === 0) {
        issues.push({
          code: 'file.empty',
          severity: 'error',
          message: t('errors.file.empty', { name: file.name }),
        });
      }
      if (!fileMatchesAcceptedType(file, acceptedFileTypes)) {
        issues.push({
          code: 'file.unsupportedType',
          severity: 'error',
          message: t('errors.file.unsupportedType', { name: file.name }),
        });
      }
      const comparableName = file.name.toLocaleLowerCase('en');
      if (!allowDuplicateNames && knownNames.has(comparableName)) {
        issues.push({
          code: 'file.duplicate',
          severity: 'error',
          message: t('errors.file.duplicate', { name: file.name }),
        });
      }

      if (issues.some(({ severity }) => severity === 'error')) {
        rejected.push({ file, issues });
      } else {
        candidates.push(file);
        knownNames.add(comparableName);
      }
    }

    try {
      const validated = await Promise.all(
        candidates.map(async (file): Promise<ValidatedPickedFile | RejectedPickedFile> => {
          let issues: readonly FilePickerIssue[] = [];
          if (validator) {
            try {
              issues = await validator(file, {
                signal: controller.signal,
                acceptedFileTypes,
              });
            } catch {
              if (controller.signal.aborted) {
                return { file, issues: [] } satisfies RejectedPickedFile;
              }
              issues = [{
                code: 'file.validationFailed',
                severity: 'error',
                message: t('errors.file.damagedPdf', { name: file.name }),
              }];
            }
          }

          if (issues.some(({ severity }) => severity === 'error')) {
            return { file, issues } satisfies RejectedPickedFile;
          }
          return {
            file,
            warnings: issues.filter(({ severity }) => severity === 'warning'),
          } satisfies ValidatedPickedFile;
        }),
      );

      if (controller.signal.aborted) {
        return;
      }

      const accepted: ValidatedPickedFile[] = [];
      for (const item of validated) {
        if ('warnings' in item) {
          accepted.push(item);
        } else {
          rejected.push(item);
        }
      }
      const allIssues = [
        ...rejected.flatMap(({ issues }) => issues),
        ...accepted.flatMap(({ warnings }) => warnings),
      ];
      setDisplayedIssues(allIssues);
      onSelection({ accepted, rejected });
    } finally {
      if (validationController.current === controller) {
        validationController.current = null;
        setValidating(false);
        onValidationStateChange?.(false);
      }
    }
  };

  const receiveFiles = (files: FileList | readonly File[]) => {
    if (disabled || isBusy || files.length === 0) {
      return;
    }
    void validateSelection(Array.from(files));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files) {
      receiveFiles(event.currentTarget.files);
    }
    event.currentTarget.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    receiveFiles(event.dataTransfer.files);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    inputRef.current?.click();
  };

  return (
    <section className="workspace-picker" aria-busy={isBusy}>
      <input
        ref={inputRef}
        id={inputId}
        className="workspace-visually-hidden"
        type="file"
        accept={accept}
        capture={capture}
        disabled={disabled || isBusy}
        multiple={multiple}
        onChange={handleInputChange}
      />
      <div
        className="workspace-picker__dropzone"
        data-drag-active={dragActive || undefined}
        data-disabled={disabled || undefined}
        role="button"
        tabIndex={disabled || isBusy ? -1 : 0}
        aria-disabled={disabled || isBusy}
        aria-describedby={`${hintId}${displayedIssues.length > 0 ? ` ${issuesId}` : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled && !isBusy) setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setDragActive(false);
          }
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
      >
        <span className="workspace-picker__icon" aria-hidden="true">
          {isBusy ? <FilePlus2 size={28} /> : <UploadCloud size={28} />}
        </span>
        <strong>{title ?? t('workflow.upload.title')}</strong>
        <span>{description ?? t('workflow.upload.dropPrompt')}</span>
        <small id={hintId}>
          {acceptedLabels === '' ? t('workflow.upload.privacyHint') : t('workflow.upload.acceptedTypes', { types: acceptedLabels })}
        </small>
        <span className="workspace-picker__privacy">
          <ShieldCheck size={15} aria-hidden="true" />
          {t('workflow.upload.privacyHint')}
        </span>
      </div>

      {displayedIssues.length > 0 && (
        <ul id={issuesId} className="workspace-messages" aria-live="polite">
          {displayedIssues.map((issue, index) => (
            <li key={`${issue.code}-${index}`} data-tone={issue.severity}>
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
